import React, { useContext } from 'react';
import classNames from 'classnames';
import { Hyperlink } from '@fx-ui/jdy-design-biz';
import { Alert, QuestionCircleFilled, Tooltip } from '@fx-ui/fine-design';
import { openInNewTab } from '@libs/shared';
import MsgConfigSection from '../msg-config-section';
import { MessageConfigItem } from '../../../../interfaces';
import { platformIntl } from '../../../../../../constants';
import { authModel, globalState } from '../../../../../../models';
import { getPurchaseLink, hasFunctionCoin, hasFunctionPlatformSms } from '../../../../../../utils';
import siteRouterManager from '../../../../../../utils/site-router';
import { MessageSettingContext } from '../../../../context';

interface SmsMsgConfigProps {
    className?: string;
    config?: MessageConfigItem;
    needCheckSmsTotal: boolean;
    onUpdateConfig: (config: MessageConfigItem) => void;
}

/**
 * 云币充值链接
 * 1、集成模式用户不能自己充值
 * 2、如果没有云币功能，则返回商务页面
 * @see FXD-49577 FXD-65167
 */
const getPurchaseCoinLink = () => {
    if (authModel?.isIntegratedCorpType || !hasFunctionCoin()) {
        return getPurchaseLink();
    }
    return siteRouterManager.getPurchaseCoinLink();
};

/**
 * 短信消息渠道配置
 */
const SmsMsgConfig: React.FC<SmsMsgConfigProps> = (props) => {
    const { className, config, needCheckSmsTotal, onUpdateConfig } = props;
    const { smsPackCount } = useContext(MessageSettingContext);
    const purchaseCoinLink = getPurchaseCoinLink();

    /**
     * 有无可用短信数量
     */
    const isSmsAvailable = smsPackCount !== 0;

    const smsEnable = needCheckSmsTotal ? isSmsAvailable && config?.enable : config?.enable;

    /**
     * 短信提醒勾选回调
     */
    const handleSmsCheck = (value: boolean) => {
        if (needCheckSmsTotal && !isSmsAvailable) {
            Alert.openAlert({
                onOk: () => {
                    openInNewTab(purchaseCoinLink, '_blank');
                },
                text4Ok: platformIntl.get('widget.phone.now.purchase'),
                text4Cancel: platformIntl.get('widget.phone.not.purchase'),
                message: platformIntl.get('msg.sms.purchase.tip'),
                title: platformIntl.get('widget.phone.purchase.tip10'),
                footerLeft: (
                    <Hyperlink
                        compact
                        underline={false}
                        target="jdy_doc"
                        href={globalState.options?.help_docs?.coin}
                    >
                        {platformIntl.get('vip.advancedFeatures')}
                    </Hyperlink>
                )
            });
        } else {
            onUpdateConfig({ ...config, enable: value });
        }
    };

    /**
     * 获取短信选择文案
     */
    const getSmsCheckText = () => {
        if (needCheckSmsTotal) {
            return (
                <Tooltip
                    style={{ maxWidth: 200 }}
                    effect="light"
                    content={platformIntl.get('msg.notification.tooltip')}
                    placement="top"
                >
                    <QuestionCircleFilled className="tip-icon" />
                </Tooltip>
            );
        }
        return <span className="sms-free">{platformIntl.get('msg.sms.free')}</span>;
    };

    if (!hasFunctionPlatformSms() || !config) {
        return null;
    }

    return (
        <MsgConfigSection
            messageChannel="sms"
            className={classNames('fx-sms-msg-config', className)}
            checkProps={{
                checked: smsEnable,
                text: platformIntl.get('sms'),
                suffix: getSmsCheckText(),
                onCheck: handleSmsCheck
            }}
            receiverProps={{
                receivers: config.target,
                onReceiverChange: (receiver) => {
                    onUpdateConfig({ ...config, target: receiver });
                }
            }}
        />
    );
};

export default SmsMsgConfig;
